import { load } from '@loaders.gl/core';
import { BitmapLayer } from '@deck.gl/layers';
import { TileLayer } from '@deck.gl/geo-layers';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import type { Layer as FulcrumLayer } from '../../../models/layer';
import type { Projection } from '../projections';
import { Projections } from '../projections';

export interface WmsBbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface TileProps {
  tile: {
    bbox: {
      west: number;
      south: number;
      east: number;
      north: number;
    };
  };
  data: unknown;
  [key: string]: unknown;
}

const SUPPORTED_PROJECTIONS = {
  'EPSG:3857': Projections['EPSG:3857'],
  'EPSG:102100': Projections['EPSG:102100'],
};

const DEFAULT_PROJECTION = Projections['EPSG:3857'];

// Params that will always be set on a GetMap request
const STATIC_GETMAP_PARAMS = {
  request: 'GetMap',
  service: 'WMS',
  format: 'image/png',
  width: '256',
  height: '256',
};

// Default values for params on a GetMap request
const DEFAULT_GETMAP_PARAMS = {
  // Different server providers have varying support how to provide the projection.
  // We just provide both srs and crs to have better interoperability.
  srs: DEFAULT_PROJECTION.name,
  crs: DEFAULT_PROJECTION.name,
  styles: '',
  bgcolor: '0xFFFFFF',
  version: '1.1.1',
  transparent: 'true',
  outline: 'false',
};

export function convertWmsLayerToMapLayer(fulcrumLayer: FulcrumLayer): TileLayer {
  try {
    const validatedUrl = new URL(fulcrumLayer.source);
    if (!validatedUrl.origin) throw new Error('Invalid origin');
  } catch (err) {
    throw new Error(`Invalid source for layer: ${fulcrumLayer.name}`);
  }

  return new TileLayer({
    id: fulcrumLayer.id,
    getTileData: (tile) => {
      try {
        // @ts-ignore
        return load(createWmsGetMapUrl(fulcrumLayer.source, tile.x, tile.y, tile.z), null);
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    minZoom: 0,
    maxZoom: 22,
    tileSize: 256,
    updateTriggers: {
      getTileData: [fulcrumLayer],
    },
    renderSubLayers: (props: TileProps) => {
      const {
        west, south, east, north,
      } = props.tile.bbox;

      return new BitmapLayer(props, {
        data: null,
        image: props.data,
        bounds: [west, south, east, north],
      });
    },
  });
}

export function createWmsGetMapUrl(urlString: string, x: number, y: number, z: number): string {
  let url: URL;
  try {
    url = new URL(urlString);
  } catch (error) {
    throw new Error(`Invalid WMS url: ${urlString}`);
  }

  const layerSourceParams: Record<string, string> = {};
  url.searchParams.forEach((value: string, key: string) => {
    layerSourceParams[key.toLowerCase()] = value;
  });

  const wmsParams = new URLSearchParams({
    ...DEFAULT_GETMAP_PARAMS,
    ...layerSourceParams,
    ...STATIC_GETMAP_PARAMS,
  });

  // Make sure srs and crs are set to the same value, prioritizing the value set in the layer source
  if (wmsParams.get('srs') !== wmsParams.get('crs')) {
    if (layerSourceParams.srs) {
      wmsParams.set('crs', layerSourceParams.srs);
    } else if (layerSourceParams.crs) {
      wmsParams.set('srs', layerSourceParams.crs);
    }
  }

  // Use a supported projection when the provided value is not supported
  // @ts-ignore
  if (!SUPPORTED_PROJECTIONS[wmsParams.get('srs')]) {
    wmsParams.set('crs', DEFAULT_PROJECTION.name);
    wmsParams.set('srs', DEFAULT_PROJECTION.name);
  }
  // @ts-ignore
  const projection = SUPPORTED_PROJECTIONS[wmsParams.get('srs')];
  const bbox = tileCoordsToWmsBbox(x, y, z, projection);
  wmsParams.set('bbox', [bbox.minX, bbox.minY, bbox.maxX, bbox.maxY].join(','));

  return `${url.origin}${url.pathname}?${wmsParams}`;
}

// Convert xyz tile coordinates to mercator bounds
export function tileCoordsToWmsBbox(x: number, y: number, z: number, projection: Projection): WmsBbox {
  const originX = projection.bounds.minX;
  const originY = projection.bounds.maxY;
  const tileSize = (-originX * 2) / (2 ** z);
  const minX = originX + (x * tileSize);
  const maxX = originX + ((x + 1) * tileSize);
  const minY = originY - ((y + 1) * tileSize);
  const maxY = originY - (y * tileSize);
  return {
    minX, minY, maxX, maxY,
  };
}

// Standard WMS GetMap params managed by the ArcGIS SDK internally.
// These must not be forwarded via customParameters to avoid duplication.
const ARCGIS_MANAGED_WMS_PARAMS = new Set([
  'service', 'request', 'version', 'layers', 'styles',
  'srs', 'crs', 'bbox', 'width', 'height',
  'format', 'transparent', 'bgcolor', 'exceptions',
]);

export function convertWMSLayerToArcGISLayer(layer):WMSLayer {
  let layerURL: URL;
  try {
    layerURL = new URL(layer.url);
    const baseURL = `${layerURL.origin}${layerURL.pathname}`;
    const params = new URLSearchParams(layerURL.search);
    const layers = params.get('layers');

    const customParameters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!ARCGIS_MANAGED_WMS_PARAMS.has(key.toLowerCase())) {
        customParameters[key] = value;
      }
    });

    return new WMSLayer({
      url: baseURL,
      sublayers: [
        {
          name: layers ?? '',
          visible: true,
        },
      ],
      ...(Object.keys(customParameters).length > 0 && { customParameters }),
    });
  } catch (e) {
    console.warn(e);
    const dummyURL = new URL('https://localhost');
    const baseURL = `${dummyURL.origin}${dummyURL.pathname}`;
    return new WMSLayer({
      url: baseURL,
      sublayers: [],
    });
  }
}
